import React, { useState, useEffect } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import UserService from "../services/user.service";
import clientService from "../services/client.service";
import workstationService from "../services/workstation.service";
import { toast } from "react-toastify";
import EventBus from "../common/EventBus";

const WorkstationManager = () => {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  const [responseCode, setResponseCode] = useState(400);
  const [workstations, setWorkstations] = useState([]);
  const [selectedWorkstations, setSelectedWorkstations] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState({});
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ show: false, message: '', onConfirm: null });
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);

  const { user: currentUser } = useSelector((state) => state.auth);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    setLoading(true);
    UserService.getClientList().then(
      (response) => {
        setContent(response.data);
        if (response.data instanceof Array && response.data.length > 0) {
          const isAdmin = currentUser && currentUser.roles && currentUser.roles.includes("ROLE_ADMIN");
          // Check if clientId is provided in URL parameters
          const clientIdFromUrl = searchParams.get("clientId");
          if (clientIdFromUrl) {
            // Verify that the clientId exists in the list
            const clientExists = response.data.some(client => client.oid === clientIdFromUrl);
            if (clientExists || isAdmin) {
              setSelectedOption(clientIdFromUrl);
            } else {
              // If clientId doesn't exist, use first client as fallback
              if (!isAdmin) {
                setSelectedOption(response.data[0].oid);
              }
            }
          } else {
            if (!isAdmin) {
              setSelectedOption(response.data[0].oid);
            }
          }
        }
        setLoading(false);
        setResponseCode(200);
      },
      (error) => {
        const _content =
          (error.response && error.response.data) ||
          error.message ||
          error.toString();
        console.log(error);
        setContent(_content);
        setResponseCode(error.response?.status || 500);
        setLoading(false);
      }
    );
  }, [searchParams, currentUser]);

  useEffect(() => {
    if (!currentUser || !selectedOption) return;

    const fetchWorkstations = async () => {
      setLoading(true);
      try {
        const response = await workstationService.getWorkstations(
          selectedOption,
          currentPage,
          pageSize
        );
        setWorkstations(response.data.content);
        setPagination({
          totalElements: response.data.totalElements,
          totalPages: response.data.totalPages,
          currentPage: response.data.number,
          pageSize: response.data.size,
          isFirst: response.data.first,
          isLast: response.data.last
        });
        setResponseCode(200);
      } catch (error) {
        const _content =
          (error.response && error.response.data) ||
          error.message ||
          error.toString();
        console.log(error);
        setContent(_content);
        setResponseCode(error.response?.status || 500);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkstations();
  }, [selectedOption, currentPage, pageSize, currentUser]);

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  const handleSelection = (event) => {
    setSelectedOption(event.target.value);
    setCurrentPage(0);
    setSelectedWorkstations([]);
  };

  const handleSearchClients = async () => {
    if (!searchTerm || searchTerm.trim() === "") {
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Please enter a search term" });
      return;
    }
    if (searchTerm.trim().length < 3) {
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Search term must be at least 3 characters long" });
      return;
    }

    setSearchLoading(true);
    try {
      const response = await clientService.searchClients(searchTerm);
      const results = response.data;
      if (!results || results.length === 0) {
        toast.info("No clients found matching the search term");
      } else if (results.length === 1) {
        // Automatically select if only 1 result
        setSelectedOption(results[0].oid);
        setSearchTerm(results[0].clientName);
        setCurrentPage(0);
        setSelectedWorkstations([]);
        toast.success(`Automatically selected client: ${results[0].clientName}`);
      } else {
        // Show modal for multiple results
        setSearchResults(results);
        setShowSearchModal(true);
      }
    } catch (error) {
      console.error("Error searching clients:", error);
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "An error occurred while searching for clients" });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchClients();
    }
  };

  const handleSelectSearchResult = (clientId, clientName) => {
    setSelectedOption(clientId);
    setSearchTerm(clientName);
    setCurrentPage(0);
    setSelectedWorkstations([]);
    setShowSearchModal(false);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setSelectedWorkstations([]);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
    setSelectedWorkstations([]);
  };

  const handleSelectWorkstation = (workstationId) => {
    setSelectedWorkstations(prev =>
      prev.includes(workstationId)
        ? prev.filter(id => id !== workstationId)
        : [...prev, workstationId]
    );
  };

  const handleSelectAll = () => {
    if (selectedWorkstations.length === workstations.length) {
      setSelectedWorkstations([]);
    } else {
      setSelectedWorkstations(workstations.map(w => w.id));
    }
  };

  const handleBatchDelete = () => {
    if (selectedWorkstations.length === 0) return;

    setConfirmDialog({
      show: true,
      message: `Are you sure you want to delete ${selectedWorkstations.length} workstation(s)?`,
      onConfirm: async () => {
        setDeleteLoading(true);
    try {
      await workstationService.batchDeleteWorkstations(selectedOption, selectedWorkstations);
      toast.success("Workstations deleted successfully");
      setSelectedWorkstations([]);
      // Refresh the list
      const response = await workstationService.getWorkstations(
        selectedOption,
        currentPage,
        pageSize
      );
      setWorkstations(response.data.content);
      setPagination({
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        currentPage: response.data.number,
        pageSize: response.data.size,
        isFirst: response.data.first,
        isLast: response.data.last
      });
    } catch (error) {
      const errorMessage = error.response?.data || error.message || "Failed to delete workstations";
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
      console.error("Batch delete failed:", error);
        } finally {
          setDeleteLoading(false);
        }
      }
    });
  };

  const handleDeleteSingle = (workstationId) => {
    setConfirmDialog({
      show: true,
      message: "Are you sure you want to delete this workstation?",
      onConfirm: async () => {
        setDeleteLoading(true);
    try {
      await workstationService.deleteWorkstation(selectedOption, workstationId);
      toast.success("Workstation deleted successfully");
      // Refresh the list
      const response = await workstationService.getWorkstations(
        selectedOption,
        currentPage,
        pageSize
      );
      setWorkstations(response.data.content);
      setPagination({
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        currentPage: response.data.number,
        pageSize: response.data.size,
        isFirst: response.data.first,
        isLast: response.data.last
      });
    } catch (error) {
      const errorMessage = error.response?.data || error.message || "Failed to delete workstation";
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
      console.error("Delete failed:", error);
        } finally {
          setDeleteLoading(false);
        }
      }
    });
  };

  const handleToggleDisable = async (workstationId) => {
    setTogglingId(workstationId);
    try {
      await workstationService.toggleDisableWorkstation(selectedOption, workstationId);
      toast.success("Workstation status updated successfully");
      // Refresh the list
      const response = await workstationService.getWorkstations(
        selectedOption,
        currentPage,
        pageSize
      );
      setWorkstations(response.data.content);
      setPagination({
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        currentPage: response.data.number,
        pageSize: response.data.size,
        isFirst: response.data.first,
        isLast: response.data.last
      });
    } catch (error) {
      const errorMessage = error.response?.data || error.message || "Failed to update workstation status";
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
      console.error("Toggle disable failed:", error);
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="container-fluid py-4 px-4 px-lg-5 mb-5">
      <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
        <h2 className="fw-bold mb-0" style={{ color: 'var(--text-main)' }}>Workstation Management</h2>
        {loading && <span className="spinner-border text-primary" role="status"></span>}
      </div>

      <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
        <div className="card-body p-4">
          <div className="row align-items-end">
            <div className="col-md-8">
              <label className="form-label fw-bold text-muted mb-2">Select Client</label>
              {currentUser && currentUser.roles && currentUser.roles.includes("ROLE_ADMIN") ? (
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control form-control-lg bg-light border-0"
                    placeholder="Search Client by Name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleSearchKeyPress}
              />
                  <button
                    className="btn-premium px-4 border-0"
                    type="button"
                    onClick={handleSearchClients}
                    disabled={searchLoading}
                  >
                    {searchLoading ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : <><i className="bi bi-search me-2"></i>Search</>}
                  </button>
            </div>
          ) : (
            <div className="dropdown">
                <select
                  value={selectedOption}
                  className="form-select form-select-lg bg-light border-0"
                  onChange={handleSelection}
                >
                  {!content && (
                    <option value="" disabled>
                      No client registration found
                    </option>
                  )}
                  {responseCode === 200 && content && content.map((client, index) => (
                    <option key={client.oid} value={client.oid}>
                      {client.clientName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            </div>
            <div className="col-md-4">
              <div className="p-3 bg-light rounded text-center h-100 d-flex flex-column justify-content-center">
                <small className="text-muted text-uppercase fw-bold mb-1">Client ID</small>
                <span className="fw-medium text-primary">{selectedOption || "—"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {responseCode >= 400 && content && (
        <div className="mb-4">
          <div className="alert alert-danger rounded-4 border-0 shadow-sm" role="alert">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {typeof content === "string"
              ? content
              : (content.hasOwnProperty("message") ? content.message : "Something went wrong")
            }
          </div>
        </div>
      )}

      {/* Batch Actions */}
      {selectedWorkstations.length > 0 && (
        <div className="mb-4">
          <div className="alert alert-info border-0 shadow-sm d-flex justify-content-between align-items-center rounded-4" style={{ backgroundColor: 'rgba(79, 70, 229, 0.1)', color: 'var(--text-main)' }}>
            <span className="fw-medium"><i className="bi bi-info-circle-fill me-2" style={{ color: '#4f46e5' }}></i>{selectedWorkstations.length} workstation(s) selected</span>
            <button
              onClick={handleBatchDelete}
              disabled={deleteLoading}
              className="btn btn-danger rounded-pill px-4"
            >
              <i className="bi bi-trash-fill me-2"></i>
              {deleteLoading ? 'Deleting...' : 'Delete Selected'}
            </button>
          </div>
        </div>
      )}

      {/* Page Size Selector & Workstation Table inside one unified Card */}
      <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px', overflow: 'hidden' }}>
        <div className="card-header bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
          <h5 className="mb-0 fw-bold text-secondary">Workstation List</h5>
          <div className="d-flex align-items-center">
            <label className="form-label mb-0 me-2 small fw-bold text-muted">Items per page:</label>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="form-select form-select-sm bg-light border-0"
              style={{ width: '80px', cursor: 'pointer' }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
              <tr>
                <th scope="col">
                  <input
                    type="checkbox"
                    checked={selectedWorkstations.length === workstations.length && workstations.length > 0}
                    onChange={handleSelectAll}
                    className="form-check-input"
                  />
                </th>
                <th scope="col">ID</th>
                <th scope="col">Name</th>
                <th scope="col">Location</th>
                <th scope="col">Created</th>
                <th scope="col">Updated</th>
                <th scope="col">Last Login</th>
                <th scope="col">IP Address</th>
                <th scope="col">Subscription</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {workstations && workstations.length > 0 ? (
                workstations.map((workstation) => (
                  <tr key={workstation.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedWorkstations.includes(workstation.id)}
                        onChange={() => handleSelectWorkstation(workstation.id)}
                        className="form-check-input"
                      />
                    </td>
                    <td>{workstation.id}</td>
                    <td>{workstation.name}</td>
                    <td>{workstation.location}</td>
                    <td>{new Date(workstation.createdAt).toLocaleDateString()}</td>
                    <td>{new Date(workstation.updatedAt).toLocaleDateString()}</td>
                    <td>
                      {workstation.lastLogin
                        ? new Date(workstation.lastLogin).toLocaleDateString()
                        : 'Never'
                      }
                    </td>
                    <td>
                      {workstation.ipAddress || <span className="text-muted">—</span>}
                    </td>
                    <td>
                      {workstation.subscription || 'No subscription'}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          onClick={() => handleToggleDisable(workstation.id)}
                          disabled={deleteLoading || togglingId === workstation.id}
                          className={`btn btn-sm ${workstation.disabled ? 'btn-success' : 'btn-warning'} d-flex align-items-center`}
                          title={workstation.disabled ? 'Enable workstation' : 'Disable workstation'}
                        >
                          {togglingId === workstation.id ? (
                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                          ) : null}
                          {workstation.disabled ? 'Enable' : 'Disable'}
                        </button>
                        <button
                          onClick={() => handleDeleteSingle(workstation.id)}
                          disabled={deleteLoading}
                          className="btn btn-sm btn-outline-danger"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center">
                    {loading ? 'Loading workstations...' : 'No workstations found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>

      {/* Pagination */}
      {pagination.totalElements != null && (
        <div className="container mt-3">
          <nav aria-label="Workstation pagination">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                {pagination.totalPages > 1 && (
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={pagination.isFirst}
                    className="btn btn-outline-primary"
                  >
                    Previous
                  </button>
                )}
              </div>

              <div className="text-center">
                <span className="badge bg-secondary">
                  {pagination.totalPages > 1
                    ? <>Page {currentPage + 1} of {pagination.totalPages} ({pagination.totalElements} total workstations)</>
                    : <>{pagination.totalElements} total workstations</>}
                </span>
              </div>

              <div>
                {pagination.totalPages > 1 && (
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={pagination.isLast}
                    className="btn btn-outline-primary"
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          </nav>
        </div>
      )}
      {/* Confirm Modal */}
      {confirmDialog.show && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div className="modal-header border-bottom-0 py-3 bg-danger text-white">
                <h5 className="modal-title fw-bold"><i className="bi bi-exclamation-triangle-fill me-2"></i>Confirm Action</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setConfirmDialog({ show: false, message: '', onConfirm: null })}></button>
              </div>
              <div className="modal-body p-4 text-center">
                <h5 className="mb-0 text-secondary">{confirmDialog.message}</h5>
              </div>
              <div className="modal-footer bg-light border-top-0 d-flex justify-content-center">
                <button type="button" className="btn btn-secondary rounded-pill px-4 me-2" onClick={() => setConfirmDialog({ show: false, message: '', onConfirm: null })}>Cancel</button>
                <button type="button" className="btn btn-danger rounded-pill px-4" onClick={() => {
                  if (confirmDialog.onConfirm) confirmDialog.onConfirm();
                  setConfirmDialog({ show: false, message: '', onConfirm: null });
                }}>Confirm</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Results Modal */}
      {showSearchModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div className="modal-header border-bottom-0 py-3" style={{ background: 'var(--primary-gradient)', color: 'white' }}>
                <h5 className="modal-title fw-bold"><i className="bi bi-search me-2"></i>Select Client</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowSearchModal(false)}
                ></button>
              </div>
              <div className="modal-body p-0" style={{ maxHeight: "400px", overflowY: "auto" }}>
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((client) => (
                        <tr key={client.oid}>
                          <td>{client.clientName}</td>
                          <td>{client.email || "-"}</td>
                          <td>{client.phone || "-"}</td>
                          <td>
                            <button
                              className="btn-premium px-3 py-1 btn-sm rounded-pill"
                              onClick={() => handleSelectSearchResult(client.oid, client.clientName)}
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer bg-light border-top-0">
                <button
                  type="button"
                  className="btn btn-secondary rounded-pill px-4"
                  onClick={() => setShowSearchModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default WorkstationManager;



